import { PropsWithChildren, useState, useRef, useEffect } from 'react'
import { Radio, RadioGroup } from '@headlessui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AvatarEditor from 'react-avatar-editor'
import { BaseModal } from 'components/organisms/modals/base-modal'
import { IconSvg } from 'components/common'
import Slider from 'rc-slider'
import { Button } from 'components/atoms/button'
import { useStore } from 'hooks/useStore'

interface Props {
	open: boolean
	setOpen?: (...args: any) => any
	onClosed?: (...args: any) => any
	fileInput: any
	imageUrl?: string
	viewBorderRadius?: number
	handleOnSubmit?: (...args: any) => any
	handleOnCancel?: (...args: any) => any
	forceRatio?: number
	shapes?: { name: string; icon?: string; width: number; height: number; value: string }[]
	showImageSourceSelection?: boolean
	imageUploadRef?: React.MutableRefObject<(() => void) | null>
	user?: any
	modalTitle?: string
}

/* const shapes = [
	{ name: 'Square', icon: 'square', width: 210, height: 210 },
	{ name: 'Vertical', icon: 'rectangle-vertical', width: 210, height: 310 },
	{ name: 'Horizontal', icon: 'rectangle-vertical', width: 250, height: 162 },
] */

export function AvatarEditorModal({
	open,
	setOpen,
	onClosed,
	fileInput,
	imageUrl,
	viewBorderRadius = 200,
	handleOnSubmit,
	handleOnCancel,
	forceRatio,
	shapes = [
		{ name: 'Square', icon: 'square', width: 210, height: 210, value: 'square' },
		{ name: 'Vertical', icon: 'rectangle-vertical', width: 210, height: 310, value: 'vertical' },
	],
	showImageSourceSelection = false,
	imageUploadRef,
	user,
	modalTitle = 'Picture Editor',
}: PropsWithChildren<Props>) {
	const editor = useRef<any>(null)
	const [scale, setScale] = useState<number>(1)
	const [rotate, setRotate] = useState<number>(0)
	const [ratio, setRatio] = useState<number>(forceRatio || 1)
	const [loading, setLoading] = useState<boolean>(false)
	const [selectedShape, setSelectedShape] = useState(shapes[0])
	const [showSourceSelection, setShowSourceSelection] = useState<boolean>(showImageSourceSelection)
	const { setBlur } = useStore()

	useEffect(() => {
		return () => {
			onClosed?.(false)
			setBlur(false)
		}
	}, [])

	useEffect(() => {
		setScale(1)
		setRotate(0)
		setShowSourceSelection(showImageSourceSelection)
		if (fileInput && !forceRatio) {
			getImageDimension(fileInput, ({ width, height }) => {
				setRatio(width / height)
			})
		} else if (imageUrl && !forceRatio) {
			getImageDimensionFromUrl(imageUrl, ({ width, height }) => {
				setRatio(width / height)
			})
		}
	}, [fileInput, imageUrl, showImageSourceSelection])

	// Reset to original state when modal closes
	useEffect(() => {
		if (!open) {
			setShowSourceSelection(showImageSourceSelection)
			setScale(1)
			setRotate(0)
		}
	}, [open, showImageSourceSelection])

	const getImageDimension = (file: any, callback: (...args: any) => any) => {
		const reader = new FileReader()

		//Read the contents of Image File.
		reader.readAsDataURL(file)
		reader.onload = function (e) {
			//Initiate the JavaScript Image object.
			const image = new Image()

			//Set the Base64 string return from FileReader as source.
			image.src = e.target?.result as string

			//Validate the File Height and Width.
			image.onload = () => {
				callback({ width: image.width, height: image.height })
			}
		}
	}

	const getImageDimensionFromUrl = (url: string, callback: (...args: any) => any) => {
		const image = new Image()
		image.crossOrigin = 'anonymous'
		image.onload = () => {
			callback({ width: image.width, height: image.height })
		}
		image.onerror = () => {
			console.error('Failed to load image from URL')
			callback({ width: 1, height: 1 })
		}
		image.src = url
	}

	return (
		<BaseModal
			open={open}
			setOpen={setOpen}
			onClose={onClosed}
			title={modalTitle}
			size="md"
			className="sm:px-8 py-8 px-4 pb-10"
			preventClose
		>
			{showSourceSelection ? (
				<div className="w-full mt-10">
					<div className="mb-8 text-center">Choose how you want to edit your profile picture:</div>
					<div className="space-y-4 max-w-[320px] mx-auto">
						{user?.avatarUrl && (
							<Button
								variant="secondary"
								fullWidth
								onClick={() => {
									setShowSourceSelection(false)
								}}
							>
								Use Existing Image
							</Button>
						)}
						<Button
							variant="primary"
							fullWidth
							onClick={() => {
								setOpen?.(false)
								if (imageUploadRef?.current) {
									imageUploadRef.current()
								}
							}}
						>
							Upload New Image
						</Button>
						<Button variant="secondary" fullWidth onClick={() => setOpen?.(false)}>
							Cancel
						</Button>
					</div>
				</div>
			) : (
				<div className="w-full mt-10">
					{(fileInput || imageUrl) && (
						<AvatarEditor
							ref={editor}
							image={fileInput || imageUrl}
							width={selectedShape.width}
							height={
								selectedShape.value === 'vertical' ? selectedShape.width / ratio : selectedShape.height
							}
							border={20}
							color={[0, 0, 0, 0.8]} // RGBA
							scale={scale}
							rotate={rotate}
							borderRadius={viewBorderRadius}
							className="mx-auto"
							disableHiDPIScaling={true}
							crossOrigin="anonymous"
						/>
					)}
					<div className="w-[250px] mx-auto mt-4 space-y-4">
						{!forceRatio && (
							<div>
								<label className="text-left block">Shape</label>
								<div className="w-full mt-3">
									<div className="w-full text-left">
										<RadioGroup
											value={selectedShape.value}
											onChange={value => {
												const shape = shapes.find(item => item.value === value)
												setSelectedShape(shape || shapes[0])
											}}
											aria-label="Image shape"
											className="space-y-2"
										>
											{shapes.map((item, index) => (
												<Radio
													key={index}
													value={item.value}
													className="group relative flex cursor-pointer rounded-lg bg-white/5 py-4 px-5 text-white shadow-md transition focus:outline-none data-[focus]:outline-1 data-[focus]:outline-white data-[checked]:bg-white/10"
												>
													<div className="flex w-full items-center justify-between">
														<div className="text-sm/6 flex items-center gap-2">
															<p className="text-white">{item.name}</p>
														</div>
														<IconSvg
															icon="radio-checked"
															className="opacity-0 transition group-data-[checked]:opacity-100"
														/>
													</div>
												</Radio>
											))}
										</RadioGroup>
									</div>
								</div>
							</div>
						)}
						<div>
							<label className="text-left block">Zoom</label>
							<div className="px-4 mt-3">
								<Slider
									min={1}
									max={10}
									step={0.1}
									defaultValue={1}
									value={scale}
									className="custom-slider-element select-none"
									onChange={(value: number | number[]) => setScale(value as number)}
								/>
							</div>
						</div>
						<div>
							<label className="text-left block">Rotate</label>
							<div className="px-4 mt-3">
								<Slider
									min={0}
									max={360}
									step={10}
									defaultValue={0}
									value={rotate}
									className="custom-slider-element select-none"
									onChange={(value: number | number[]) => setRotate(value as number)}
								/>
							</div>
						</div>
					</div>
					<div className="mt-16 max-w-[320px] mx-auto">
						<div className="text-center space-y-4">
							{(fileInput || imageUrl) && (
								<button
									type="button"
									className="px-6 py-3 rounded-3xl text-center transition-all duration-300 bg-primary text-white font-bold block w-full h-12 shadow-button backdrop-blur-lg hover:bg-primary-hover disabled:bg-white/10 disabled:text-white-25 disabled:shadow-none"
									onClick={() => {
										const dataUrl = editor.current?.getImage().toDataURL()
										setLoading(true)
										fetch(dataUrl)
											.then(res => res.blob())
											.then((blob: any) => {
												const fileName = fileInput
													? fileInput.name
													: `edited-avatar-${Date.now()}.png`
												const fileType = fileInput ? fileInput.type : 'image/png'
												const fileOutput = new File([blob], fileName, {
													type: fileType,
												})
												handleOnSubmit?.(fileOutput, dataUrl)
												setOpen?.(false)
											})
											.finally(() => {
												setLoading(false)
											})
									}}
									disabled={!editor || loading}
								>
									{loading && (
										<FontAwesomeIcon icon={['fas', 'spinner']} size="lg" spin className="mr-1" />
									)}
									Save
								</button>
							)}
							<Button
								variant="secondary"
								fullWidth
								size="lg"
								onClick={() => {
									setOpen?.(false)
									onClosed?.(false)
									setBlur(false)
									handleOnCancel?.()
								}}
								disabled={loading}
							>
								Cancel
							</Button>
						</div>
					</div>
				</div>
			)}
		</BaseModal>
	)
}
